/**
 * Phase 3 — Story Detail Redesign (Bermuda Bliss).
 *
 * Unified detail page for BOTH a chapter story record and a booked-trip
 * record. Mounted at `/chapters/:year/trip/:id`. Resolves the entity by
 * trying the story fetch first, falling back to the trip fetch — the
 * two id spaces are disjoint (chapters vs booked_trips collections)
 * so the winner is unambiguous.
 *
 * Preserves every action available from the old ChapterYearPage RowMenu
 * (edit, delete, move, share) and every existing sub-flow for journal /
 * itinerary / packing / shopping — those are rendered inline in tabs.
 *
 * Design goals (per spec):
 *   • Photo carousel with add-photo + counter + dots (top).
 *   • Highlights card with 16-icon picker (below carousel).
 *   • 5 tabs — Journal / Itinerary / Travel Docs / Packing / Shopping.
 *   • Edit mode (`?edit=1`) — inline-editable header + trash icons on
 *     photos + trash chips on highlights + sticky Save/Cancel bar.
 *
 * The page NEVER touches Booking.com URL logic, mobile code, or the
 * auto-promotion pipeline.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft, Share2, MoreHorizontal, Sparkles, MapPin, CalendarDays,
  ChevronLeft, ChevronRight, Plus, Trash2, X as XIcon, FileText, Star,
  NotebookPen, ListTodo, Backpack, ShoppingBag, Save,
  Pencil, MoveRight, TrendingUp, Wallet,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import UploadPhotoButton from "@/components/UploadPhotoButton";
import MoveDialog from "@/components/MoveDialog";
import ShareChapterDialog from "@/components/ShareChapterDialog";
import BackpackList from "@/components/BackpackList";
import { HIGHLIGHT_ICONS, HighlightIconGlyph } from "@/lib/highlightIcons";

const TIDS = {
  page: "trip-story-detail-page",
  header: "tsd-header",
  back: "tsd-back-btn",
  share: "tsd-share-btn",
  menu: "tsd-menu",
  editMode: "tsd-edit-mode",
  saveBtn: "tsd-save-btn",
  cancelBtn: "tsd-cancel-btn",
  carousel: "tsd-carousel",
  addPhotos: "tsd-add-photos",
  highlightsCard: "tsd-highlights-card",
  addHighlight: "tsd-add-highlight",
  tabs: "tsd-tabs",
  tab: (id) => `tsd-tab-${id}`,
  bookedBadge: "tsd-booked-badge",
  statePill: "tsd-state-pill",
  title: "tsd-title",
  location: "tsd-location",
  dates: "tsd-dates",
  description: "tsd-description",
};

function fmtDateLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function fmtDateRangeLong(startIso, endIso) {
  if (!startIso) return "";
  const d1 = new Date(startIso);
  if (isNaN(d1)) return "";
  const y = d1.getFullYear();
  if (!endIso) return `${fmtDateLong(startIso)}, ${y}`;
  const d2 = new Date(endIso);
  if (isNaN(d2)) return `${fmtDateLong(startIso)}, ${y}`;
  return `${fmtDateLong(startIso)} – ${fmtDateLong(endIso)}, ${d2.getFullYear()}`;
}

function tripYearOf(rec) {
  const s = rec?.start_date || rec?.end_date || "";
  return parseInt(String(s).slice(0, 4), 10) || new Date().getFullYear();
}

// ==================================================================
//  DetailActionMenu — replaces the old shared <RowMenu> on this page
//  so we can surface every action (Edit / Share / Move / Promote /
//  Delete) in one place. Custom popover; ESC + outside-click closes.
// ==================================================================
function DetailActionMenu({
  testId, onEdit, onShare, onMove, onDelete, onPromote,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => { if (ev.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = [
    { key: "edit",    label: "Edit",              Icon: Pencil,      onClick: onEdit },
    { key: "share",   label: "Share",             Icon: Share2,      onClick: onShare },
    { key: "move",    label: "Move to another year", Icon: MoveRight, onClick: onMove },
    onPromote && { key: "promote", label: "Promote to Story", Icon: TrendingUp, onClick: onPromote },
    { key: "delete",  label: "Delete",            Icon: Trash2,      onClick: onDelete, danger: true },
  ].filter(Boolean);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full bg-white border-2 border-tb-navy text-tb-navy flex items-center justify-center tb-tap hover:bg-tb-navy hover:text-white transition-colors"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`${testId}-menu`}
          className="absolute right-0 mt-2 w-56 rounded-xl bg-white border border-tb-navy/15 shadow-[0_10px_30px_-8px_rgba(26,58,95,0.35)] py-1 z-30"
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              data-testid={`${testId}-${it.key}`}
              onClick={() => { setOpen(false); it.onClick && it.onClick(); }}
              className={
                "w-full flex items-center gap-2.5 px-3 py-2 font-hand text-sm tb-tap text-left " +
                (it.danger
                  ? "text-red-600 hover:bg-red-50"
                  : "text-tb-navy hover:bg-tb-navy/5")
              }
            >
              <it.Icon className="w-4 h-4" />
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Try to fetch either a story or a booked-trip by id, in that order.
 * Returns `{ kind: "story" | "trip", record }` or throws.
 */
async function loadEntity(id) {
  // Chapters list — pull all + client-side filter (no id endpoint).
  try {
    const { data } = await api.get("/chapters");
    // Backend returns `{ years, by_year: [{year, journeys, ...}] }`
    const allStories = [];
    for (const y of data?.by_year || []) {
      for (const j of y.journeys || []) allStories.push(j);
    }
    const story = allStories.find((s) => s.id === id);
    if (story) return { kind: "story", record: story };
  } catch { /* fall through */ }
  // Booked trip fetch
  try {
    const { data } = await api.get(`/booked-trips/${id}`);
    return { kind: "trip", record: data };
  } catch (e) {
    throw e;
  }
}

// ==================================================================
//  Header (title, dates, state pill, badges, actions)
// ==================================================================
function DetailHeader({
  rec, kind, year, editing,
  onBack, onShare, onEdit, onDelete, onMove, onPromote, onChangeField,
}) {
  const isTrip = kind === "trip";
  const title = isTrip ? (rec.name || rec.destination_name || "Trip") : rec.trip_name;
  const location = [rec.destination_name, rec.country].filter(Boolean).join(", ")
    || rec.location || rec.country || "";
  const start = rec.start_date, end = rec.end_date;
  const dateRange = fmtDateRangeLong(start, end);
  const description = rec.description
    || (rec.favorite_moments || []).find((m) => (m || "").trim())
    || rec.notes || "";

  // State pill computation (trips only).
  const now = new Date().toISOString().slice(0, 10);
  const s = String(start || "").slice(0, 10);
  const e = String(end || "").slice(0, 10);
  const isHappening = isTrip && s && s <= now && (!e || now <= e);
  const isUpcoming = isTrip && s && s > now;

  // Booked-with badge — same rule as chapter card (#39).
  const promoted = kind === "story"
    ? (rec.source === "booked_trip" || !!rec.auto_promoted_from_trip_id)
    : true; // every booked trip qualifies for the gold pill by definition

  return (
    <div
      data-testid={TIDS.header}
      className="pt-4 md:pt-6 pb-4 border-b border-tb-navy/10"
    >
      {/* Row 1: back + share + menu */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          data-testid={TIDS.back}
          onClick={onBack}
          className="inline-flex items-center gap-1.5 font-serif-it italic text-tb-navy hover:text-tb-navy-deep text-base md:text-lg tb-tap"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Chapter {year}</span>
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={TIDS.share}
            onClick={onShare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy text-tb-navy font-hand text-xs tb-tap hover:bg-tb-navy hover:text-white transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
          <DetailActionMenu
            testId={TIDS.menu}
            onEdit={onEdit}
            onShare={onShare}
            onMove={onMove}
            onDelete={onDelete}
            onPromote={isTrip && !rec.promoted_story_id ? onPromote : undefined}
          />
        </div>
      </div>

      {/* Row 2: badges + title */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {promoted && (
          <span
            data-testid={TIDS.bookedBadge}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tb-navy-deep text-tb-cream font-hand text-[10px] tracking-wide"
          >
            <Star className="w-2.5 h-2.5 text-tb-gold-bright" fill="currentColor" strokeWidth={0} />
            booked with TravelBio
          </span>
        )}
      </div>

      {editing ? (
        <input
          data-testid={`${TIDS.title}-input`}
          className="w-full font-serif-it italic text-tb-navy text-3xl sm:text-4xl md:text-5xl leading-tight bg-transparent border-b-2 border-dashed border-tb-navy/40 focus:outline-none focus:border-tb-navy px-1 py-1"
          value={title}
          onChange={(e) => onChangeField(kind === "story" ? "trip_name" : "name", e.target.value)}
          placeholder="Trip name"
        />
      ) : (
        <h1
          data-testid={TIDS.title}
          className="font-serif-it italic text-tb-navy text-3xl sm:text-4xl md:text-5xl leading-tight"
        >
          {title || "Untitled trip"}
        </h1>
      )}

      {/* Metadata row */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-tb-navy/70 font-hand text-sm">
        <span className="inline-flex items-center gap-1" data-testid={TIDS.location}>
          <MapPin className="w-3.5 h-3.5 text-tb-navy" />
          {editing ? (
            <input
              data-testid={`${TIDS.location}-input`}
              value={location}
              onChange={(e) => {
                // Store into destination_name to keep parity with existing schema.
                onChangeField("destination_name", e.target.value);
              }}
              className="bg-transparent border-b border-dashed border-tb-navy/40 focus:outline-none focus:border-tb-navy px-1"
              placeholder="Location"
            />
          ) : (
            <span>{location || "—"}</span>
          )}
        </span>
        <span className="inline-flex items-center gap-1" data-testid={TIDS.dates}>
          <CalendarDays className="w-3.5 h-3.5 text-tb-navy" />
          {editing ? (
            <>
              <input
                type="date"
                value={start || ""}
                onChange={(e) => onChangeField("start_date", e.target.value)}
                className="bg-transparent border-b border-dashed border-tb-navy/40 focus:outline-none focus:border-tb-navy px-1"
              />
              <span>–</span>
              <input
                type="date"
                value={end || ""}
                onChange={(e) => onChangeField("end_date", e.target.value)}
                className="bg-transparent border-b border-dashed border-tb-navy/40 focus:outline-none focus:border-tb-navy px-1"
              />
            </>
          ) : (
            <span>{dateRange || "Dates coming soon"}</span>
          )}
        </span>
      </div>

      {/* Description */}
      <div className="mt-3">
        {editing ? (
          <textarea
            data-testid={`${TIDS.description}-input`}
            value={description}
            onChange={(e) => onChangeField(isTrip ? "notes" : "description", e.target.value)}
            placeholder="A few words about this trip…"
            rows={2}
            className="w-full bg-white border border-tb-navy/20 focus:border-tb-navy rounded-lg px-3 py-2 font-ui text-sm outline-none resize-y"
          />
        ) : description ? (
          <p data-testid={TIDS.description} className="font-ui text-sm md:text-base text-tb-navy/85">
            {description}
          </p>
        ) : null}
      </div>

      {/* State pill */}
      {(isHappening || isUpcoming) && !editing && (
        <div className="mt-3">
          {isHappening ? (
            <span
              data-testid={TIDS.statePill}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-gold text-tb-navy-deep font-hand text-xs"
            >
              <Sparkles className="w-3 h-3" /> Happening now
            </span>
          ) : (
            <span
              data-testid={TIDS.statePill}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border-2 border-tb-navy/50 text-tb-navy/70 font-hand text-xs"
            >
              Upcoming
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ==================================================================
//  Photo carousel (full-width, dots, counter, arrows, add-photo)
// ==================================================================
function PhotoCarouselSection({ photos, onAdd, onRemove, editing, coverPhotoId, onSetCover }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { if (idx >= photos.length) setIdx(Math.max(0, photos.length - 1)); }, [photos.length, idx]);
  const hasPhotos = photos.length > 0;
  // 2026-07-18 Fix 3a — currently-selected cover URL. If unset, the
  // implicit default is `photos[0]`. Users can pin any photo as the
  // cover with the star button on each tile.
  const currentCover = coverPhotoId || photos[0] || null;
  const isCurrentCover = hasPhotos && photos[idx] === currentCover;
  const canPinCover = photos.length >= 2;

  return (
    <div data-testid={TIDS.carousel} className="mt-6 md:mt-8">
      <div
        className={
          "relative w-full overflow-hidden rounded-2xl bg-white aspect-[16/9] " +
          (hasPhotos ? "border border-tb-navy/20" : "border-2 border-dashed border-tb-navy/25")
        }
      >
        {hasPhotos ? (
          <>
            <img
              src={photos[idx]}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  data-testid="tsd-carousel-prev"
                  onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
                  aria-label="Previous photo"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-tb-navy/80 hover:bg-tb-navy text-white flex items-center justify-center tb-tap"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  data-testid="tsd-carousel-next"
                  onClick={() => setIdx((i) => (i + 1) % photos.length)}
                  aria-label="Next photo"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-tb-navy/80 hover:bg-tb-navy text-white flex items-center justify-center tb-tap"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            <div
              data-testid="tsd-carousel-counter"
              className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-tb-navy/80 text-white font-hand text-xs"
            >
              {idx + 1} / {photos.length}
            </div>
            {/* 2026-07-18 Fix 3a — Set-as-cover affordance. Only shown
                when there are 2+ photos (a single-photo story doesn't
                need a picker). Currently-selected cover gets a filled
                gold star + `Cover` label. Clicking sets that photo as
                cover via the /cover endpoint. */}
            {canPinCover && onSetCover && (
              <button
                type="button"
                data-testid={`tsd-set-cover-${idx}`}
                onClick={() => onSetCover(photos[idx])}
                aria-label={isCurrentCover ? "Already the cover photo" : "Set as cover photo"}
                className={
                  "absolute bottom-3 right-3 inline-flex items-center gap-1 pl-2 pr-2.5 py-1 rounded-full text-xs font-hand tb-tap transition-colors border-2 " +
                  (isCurrentCover
                    ? "bg-tb-gold text-tb-navy-deep border-tb-gold"
                    : "bg-tb-navy/80 text-white border-tb-navy/80 hover:bg-tb-navy")
                }
              >
                <Star className="w-3.5 h-3.5" fill={isCurrentCover ? "currentColor" : "none"} strokeWidth={1.8} />
                {isCurrentCover ? "Cover" : "Set as cover"}
              </button>
            )}
            {editing && (
              <button
                type="button"
                data-testid={`tsd-remove-photo-${idx}`}
                onClick={() => onRemove(photos[idx])}
                aria-label="Remove this photo"
                className="absolute top-3 left-3 w-8 h-8 rounded-full bg-red-500/90 hover:bg-red-600 text-white flex items-center justify-center tb-tap"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-tb-navy/45">
            <ChevronRight className="w-6 h-6 text-tb-navy/35" aria-hidden="true" />
            <span className="font-serif-it italic text-xl md:text-2xl text-tb-navy/45">No photos yet</span>
          </div>
        )}
      </div>
      {/* Dots + add button below the carousel */}
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {photos.slice(0, 12).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Go to photo ${i + 1}`}
              className={
                "w-2 h-2 rounded-full transition-all " +
                (i === idx ? "bg-tb-navy scale-125" : "bg-tb-navy/20 hover:bg-tb-navy/50")
              }
            />
          ))}
        </div>
        <div data-testid={TIDS.addPhotos} className="min-w-[180px]">
          <UploadPhotoButton
            testId="tsd-photo-uploader"
            value={photos}
            onChange={(next) => {
              // Compute the diff — added URLs get appended one by one via
              // the append endpoint; removed URLs go through the trash-icon
              // path so no work needed here for removes.
              const before = new Set(photos);
              for (const url of next) if (!before.has(url)) onAdd(url);
            }}
            max={30}
            label={photos.length ? "+ Add photos" : "+ Add photos"}
            overflowMessage="Reached 30 photos on this trip"
          />
        </div>
      </div>
    </div>
  );
}

// ==================================================================
//  Highlights card + add-highlight modal
// ==================================================================
function HighlightsCard({ highlights, tripStart, tripEnd, onAdd, onRemove, editing }) {
  const [showAll, setShowAll] = useState(false);
  const [picking, setPicking] = useState(false);
  const visible = showAll ? highlights : highlights.slice(0, 4);
  const overflow = highlights.length > 4;

  return (
    <div
      data-testid={TIDS.highlightsCard}
      className="mt-6 md:mt-8 rounded-2xl border border-tb-navy/15 bg-white shadow-[0_4px_20px_-8px_rgba(26,58,95,0.15)] p-4 md:p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif-it italic text-tb-navy text-xl md:text-2xl inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-tb-gold-bright" />
          Highlights
        </h2>
        {overflow && (
          <button
            type="button"
            data-testid="tsd-highlights-viewall"
            onClick={() => setShowAll((v) => !v)}
            className="font-hand text-xs text-tb-navy hover:text-tb-navy-deep underline underline-offset-2 tb-tap"
          >
            {showAll ? "Show less" : `View all (${highlights.length})`}
          </button>
        )}
      </div>
      {highlights.length === 0 ? (
        <button
          type="button"
          data-testid="tsd-highlights-empty"
          onClick={() => setPicking(true)}
          className="w-full rounded-xl border-2 border-dashed border-tb-navy/25 hover:border-tb-navy p-6 flex flex-col items-center justify-center gap-1.5 text-tb-navy/60 hover:text-tb-navy tb-tap transition-colors"
        >
          <Plus className="w-5 h-5 text-tb-navy" />
          <span className="font-hand text-sm">Add a highlight</span>
        </button>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {visible.map((h) => (
            <div
              key={h.id}
              data-testid={`tsd-highlight-${h.id}`}
              className="relative rounded-xl bg-white border border-tb-navy/15 hover:border-tb-navy/40 transition-colors p-3 flex flex-col items-center text-center"
            >
              <HighlightIconGlyph slug={h.icon} className="w-7 h-7 text-tb-gold-bright" />
              <div className="mt-1.5 font-serif-it italic text-tb-navy text-sm leading-tight truncate max-w-full">
                {h.label}
              </div>
              {h.date && (
                <div className="mt-0.5 font-hand text-[10px] text-tb-navy/50">
                  {fmtDateLong(h.date)}
                </div>
              )}
              {editing && (
                <button
                  type="button"
                  data-testid={`tsd-remove-highlight-${h.id}`}
                  onClick={() => onRemove(h)}
                  aria-label={`Remove highlight ${h.label}`}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center tb-tap"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            data-testid={TIDS.addHighlight}
            onClick={() => setPicking(true)}
            className="rounded-xl border-2 border-dashed border-tb-navy/25 hover:border-tb-navy p-3 flex flex-col items-center justify-center gap-1 text-tb-navy/60 hover:text-tb-navy tb-tap transition-colors min-h-[92px]"
          >
            <Plus className="w-5 h-5 text-tb-navy" />
            <span className="font-hand text-xs">Add highlight</span>
          </button>
        </div>
      )}
      {picking && (
        <HighlightPickerModal
          tripStart={tripStart}
          tripEnd={tripEnd}
          onClose={() => setPicking(false)}
          onSave={async (payload) => { await onAdd(payload); setPicking(false); }}
        />
      )}
    </div>
  );
}

function HighlightPickerModal({ tripStart, tripEnd, onSave, onClose }) {
  const [icon, setIcon] = useState("star");
  const [label, setLabel] = useState("");
  const [date, setDate] = useState(tripStart || "");
  const submit = (e) => {
    e.preventDefault();
    const l = label.trim();
    if (!l) return toast.error("Label is required");
    if (date && tripStart && date < tripStart) {
      return toast.error("Date should be within the trip range");
    }
    if (date && tripEnd && date > tripEnd) {
      return toast.error("Date should be within the trip range");
    }
    onSave({ icon, label: l.slice(0, 40), date });
  };
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3">
      <form
        onSubmit={submit}
        role="dialog" aria-modal="true"
        data-testid="tsd-highlight-picker"
        className="bg-white rounded-2xl border border-tb-navy/15 max-w-md w-full max-h-[92vh] overflow-y-auto"
      >
        <div className="bg-tb-navy text-white px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">add a highlight</span>
          <button type="button" onClick={onClose} className="font-hand text-white/80 hover:text-white tb-tap">close</button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <div className="font-hand text-tb-navy uppercase tracking-wider text-[10px] mb-2">Icon</div>
            <div className="grid grid-cols-8 gap-2">
              {HIGHLIGHT_ICONS.map((h) => {
                const active = icon === h.slug;
                const Icon = h.Icon;
                return (
                  <button
                    key={h.slug}
                    type="button"
                    data-testid={`tsd-icon-${h.slug}`}
                    onClick={() => setIcon(h.slug)}
                    aria-label={h.label}
                    className={
                      "aspect-square rounded-lg flex items-center justify-center tb-tap border-2 transition-colors " +
                      (active
                        ? "bg-tb-navy text-white border-tb-navy"
                        : "bg-white text-tb-navy border-tb-navy/15 hover:border-tb-navy/50")
                    }
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                  </button>
                );
              })}
            </div>
          </div>
          <label className="block">
            <span className="font-hand text-tb-navy uppercase tracking-wider text-[10px]">Label</span>
            <input
              required
              data-testid="tsd-highlight-label"
              value={label}
              maxLength={40}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="the espresso at the corner café"
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
            />
          </label>
          <label className="block">
            <span className="font-hand text-tb-navy uppercase tracking-wider text-[10px]">Date</span>
            <input
              type="date"
              data-testid="tsd-highlight-date"
              value={date || ""}
              min={tripStart || undefined}
              max={tripEnd || undefined}
              onChange={(e) => setDate(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
            />
          </label>
          <button
            type="submit"
            data-testid="tsd-highlight-save"
            className="w-full py-2.5 rounded-xl bg-tb-navy text-white border-2 border-tb-navy hover:bg-tb-navy-deep font-hand text-lg tb-tap transition-colors"
          >
            save highlight
          </button>
        </div>
      </form>
    </div>
  );
}

// ==================================================================
//  Tab bar + tab content
// ==================================================================
const TABS_BASE = [
  { id: "journal",  label: "Journal",        Icon: NotebookPen },
  { id: "itinerary",label: "Itinerary",      Icon: CalendarDays },
  { id: "docs",     label: "Travel Docs",    Icon: FileText },
  { id: "packing",  label: "Packing Lists",  Icon: Backpack },
  { id: "shopping", label: "Shopping Lists", Icon: ShoppingBag },
];
/**
 * 2026-07-17 — Budget tab appears only when the record is a booked trip
 * (or a promoted story whose source trip still exists). Stories without
 * a linked trip can't hit `/booked-trips/{id}/expenses` so the tab is
 * hidden. Insertion index 3 lands it between Travel Docs and Packing.
 */
function tabsForContext({ hasBudget }) {
  if (!hasBudget) return TABS_BASE;
  const budgetTab = { id: "budget", label: "Budget", Icon: Wallet };
  return [...TABS_BASE.slice(0, 3), budgetTab, ...TABS_BASE.slice(3)];
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div
      data-testid={TIDS.tabs}
      role="tablist"
      className="mt-6 md:mt-8 border-b border-tb-navy/15 flex flex-wrap gap-1 md:gap-2"
    >
      {tabs.map((t) => {
        const isActive = active === t.id;
        const Icon = t.Icon;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={isActive}
            data-testid={TIDS.tab(t.id)}
            onClick={() => onChange(t.id)}
            className={
              "inline-flex items-center gap-1.5 px-3 md:px-4 py-2 -mb-px border-b-[3px] font-serif-it italic text-sm md:text-base tb-tap transition-colors " +
              (isActive
                ? "border-tb-navy text-tb-navy"
                : "border-transparent text-tb-navy/45 hover:text-tb-navy")
            }
          >
            <Icon className={"w-4 h-4 " + (isActive ? "text-tb-navy" : "text-tb-navy/45")} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// --- Journal tab -------------------------------------------------
function JournalTab({ kind, rec, tripId, reload }) {
  // 2026-07-17: edit affordances now render whenever a `tripId` is
  // resolvable — that includes promoted stories whose journal was
  // mirrored from a still-existing source trip. Backend writes ALWAYS
  // target `/booked-trips/{tripId}/journal`; the story's cached copy
  // will be re-synced via `reload()` on the next parent refresh.
  const canEdit = !!tripId;
  const isTrip = kind === "trip";
  const entries = useMemo(() => {
    const src = isTrip ? (rec.journal || []) : (rec.journal_entries || []);
    return [...src].sort((a, b) => (a.date || "") < (b.date || "") ? -1 : 1);
  }, [rec, isTrip]);

  // Composer state — when `editingDate` is set we're re-saving that
  // date (PUT upserts by date, so editing = same-date PUT with new
  // text). Cancel clears the composer and returns to "new entry" mode.
  const [draft, setDraft] = useState({ date: rec.start_date || "", text: "" });
  const [editingDate, setEditingDate] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!canEdit) return;
    const text = (draft.text || "").trim();
    if (!text || !draft.date) return toast.error("Pick a date and add some words");
    setBusy(true);
    try {
      // If user changed the date while editing an existing entry, we
      // upsert the new date and delete the old one to avoid stale
      // duplicates. Same-date edits are a single PUT.
      if (editingDate && editingDate !== draft.date) {
        await api.delete(`/booked-trips/${tripId}/journal/${editingDate}`);
      }
      await api.put(`/booked-trips/${tripId}/journal`, { date: draft.date, text });
      setDraft({ date: rec.start_date || "", text: "" });
      setEditingDate(null);
      toast.success(editingDate ? "Entry updated" : "Entry saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  const startEdit = (entry) => {
    setDraft({ date: entry.date || "", text: entry.text || "" });
    setEditingDate(entry.date || null);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => {
    setDraft({ date: rec.start_date || "", text: "" });
    setEditingDate(null);
  };

  const remove = async (entry) => {
    if (!canEdit) return;
    if (!window.confirm("Delete this journal entry?")) return;
    try {
      await api.delete(`/booked-trips/${tripId}/journal/${entry.date}`);
      toast.success("Entry deleted");
      if (editingDate === entry.date) cancelEdit();
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  return (
    <div data-testid="tsd-tab-journal-body" className="py-4 space-y-4">
      {canEdit && (
        <div className="rounded-2xl border border-tb-navy/15 bg-white p-4 shadow-[0_2px_10px_-4px_rgba(26,58,95,0.15)]">
          <div className="flex items-center justify-between mb-2">
            <span className="font-serif-it italic text-tb-navy text-lg">
              {editingDate ? "Edit Entry" : "New Entry"}
            </span>
            <input
              type="date"
              value={draft.date || ""}
              onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
              min={rec.start_date || undefined}
              max={rec.end_date || undefined}
              className="px-2 py-1 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy font-hand text-xs outline-none text-tb-navy"
              data-testid="tsd-journal-date"
            />
          </div>
          <textarea
            rows={4}
            value={draft.text}
            onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
            placeholder="the sea. the coffee. the taxi driver's story."
            className="w-full bg-white border border-tb-navy/20 focus:border-tb-navy rounded-lg px-3 py-2 font-ui text-sm outline-none resize-y text-tb-navy"
            data-testid="tsd-journal-text"
          />
          <div className="mt-2 flex justify-end gap-2">
            {editingDate && (
              <button
                type="button"
                onClick={cancelEdit}
                data-testid="tsd-journal-cancel"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-tb-navy border-2 border-tb-navy hover:bg-tb-navy hover:text-white font-hand text-xs tb-tap transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="tsd-journal-save"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-navy text-white border-2 border-tb-navy hover:bg-tb-navy-deep font-hand text-xs tb-tap disabled:opacity-60 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> {editingDate ? "Save changes" : "New Entry"}
            </button>
          </div>
        </div>
      )}
      {entries.length === 0 ? (
        <div className="py-6 text-center font-hand text-tb-navy/50 text-sm">
          No journal entries yet
        </div>
      ) : (
        <ol className="space-y-4">
          {entries.map((j, i) => (
            <li
              key={j.id || `${j.date}-${i}`}
              data-testid={`tsd-journal-entry-${i}`}
              className="rounded-xl bg-white border border-tb-navy/15 p-3"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-hand text-tb-navy uppercase tracking-wider text-[10px]">
                  {fmtDateLong(j.date)}
                </span>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(j)}
                      aria-label="Edit entry"
                      data-testid={`tsd-journal-edit-${i}`}
                      className="text-tb-navy/60 hover:text-tb-navy tb-tap p-1 rounded hover:bg-tb-navy/5"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(j)}
                      aria-label="Delete entry"
                      data-testid={`tsd-journal-delete-${i}`}
                      className="text-tb-navy/60 hover:text-red-500 tb-tap p-1 rounded hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <p className="font-ui text-sm text-tb-navy/90 whitespace-pre-wrap">{j.text}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// --- Itinerary tab -----------------------------------------------
function ItineraryTab({ kind, rec, tripId, reload }) {
  // 2026-07-17: like Journal, edit affordances render whenever a
  // tripId is resolvable — covers both trip records and promoted
  // stories with an intact source trip.
  const canEdit = !!tripId;
  const byDay = useMemo(() => {
    const m = {};
    const items = rec.itinerary || [];
    for (const it of items) {
      const d = it.day ?? 1;
      if (!m[d]) m[d] = [];
      m[d].push(it);
    }
    return m;
  }, [rec.itinerary]);
  const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);
  const [form, setForm] = useState({ day: 1, title: "" });

  const add = async () => {
    const t = (form.title || "").trim();
    if (!t) return toast.error("Add a title");
    if (!canEdit) return toast.error("Itinerary is edited from the source trip");
    try {
      await api.post(`/booked-trips/${tripId}/itinerary`, {
        day: parseInt(form.day, 10) || 1, title: t,
      });
      setForm({ day: form.day, title: "" });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add");
    }
  };
  const remove = async (id) => {
    if (!canEdit) return;
    if (!window.confirm("Remove this itinerary item?")) return;
    try {
      await api.delete(`/booked-trips/${tripId}/itinerary/${id}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove");
    }
  };

  return (
    <div data-testid="tsd-tab-itinerary-body" className="py-4 space-y-4">
      {canEdit && (
        <div className="rounded-2xl border border-tb-navy/15 bg-white p-3 flex flex-wrap items-center gap-2 shadow-[0_2px_10px_-4px_rgba(26,58,95,0.15)]">
          <input
            type="number"
            min={1}
            max={99}
            value={form.day}
            onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
            className="w-16 px-2 py-1.5 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
            data-testid="tsd-itin-day"
          />
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Louvre + Tuileries walk"
            className="flex-1 min-w-[140px] px-3 py-1.5 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
            data-testid="tsd-itin-title"
          />
          <button
            type="button"
            onClick={add}
            data-testid="tsd-itin-add"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy text-white border-2 border-tb-navy hover:bg-tb-navy-deep font-hand text-xs tb-tap transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Item
          </button>
        </div>
      )}
      {days.length === 0 ? (
        <div className="py-6 text-center font-hand text-tb-navy/50 text-sm">No itinerary items yet</div>
      ) : days.map((d) => (
        <div key={d} data-testid={`tsd-itin-day-${d}`}>
          <div className="font-serif-it italic text-tb-navy text-lg mb-2">Day {d}</div>
          <ul className="space-y-1.5">
            {byDay[d].map((it) => (
              <li
                key={it.id}
                data-testid={`tsd-itin-item-${it.id}`}
                className="rounded-lg bg-white border border-tb-navy/15 px-3 py-2 flex items-start justify-between gap-2"
              >
                <div>
                  <div className="font-ui text-sm text-tb-navy/90">{it.title}</div>
                  {it.notes && <div className="font-hand text-tb-navy/50 text-xs">{it.notes}</div>}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => remove(it.id)}
                    data-testid={`tsd-itin-delete-${it.id}`}
                    className="text-tb-navy/60 hover:text-red-500 tb-tap p-1 rounded hover:bg-red-50"
                    aria-label="Remove item"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// --- Travel Docs (placeholder) -----------------------------------
function TravelDocsTab() {
  return (
    <div
      data-testid="tsd-tab-docs-body"
      className="py-10 flex flex-col items-center text-center gap-3"
    >
      <div className="w-14 h-14 rounded-full bg-white border-2 border-tb-navy/15 flex items-center justify-center">
        <FileText className="w-7 h-7 text-tb-navy" strokeWidth={1.4} />
      </div>
      <div className="font-serif-it italic text-tb-navy text-xl">
        Coming soon — Travel documents upload is on the way.
      </div>
      <p className="font-hand text-tb-navy/55 text-sm max-w-md">
        Passport scans, e-visas, ticket PDFs and hotel confirmations will land here.
      </p>
    </div>
  );
}

// --- Packing (reuse BackpackList) --------------------------------
function PackingTab({ tripId }) {
  return (
    <div data-testid="tsd-tab-packing-body" className="py-4">
      {tripId ? (
        <BackpackList tripId={tripId} mode="full" />
      ) : (
        <div className="py-6 text-center font-hand text-tb-navy/50 text-sm">
          Packing list is available on booked trips.
        </div>
      )}
    </div>
  );
}

// --- Shopping (embedded per-trip list + link to unified page) -----
// The app's unified backend stores packing + shopping items in one
// collection scoped to a trip via `trip_ids`. So the Shopping tab
// renders the same `<BackpackList>` used by Packing — it's the same
// underlying data. Users who want a cross-trip view still get the
// outbound link.
function ShoppingTab({ tripId }) {
  return (
    <div data-testid="tsd-tab-shopping-body" className="py-4 space-y-4">
      {tripId ? (
        <>
          <BackpackList tripId={tripId} mode="full" />
          <div className="pt-2 border-t border-tb-navy/10 flex items-center justify-between gap-3">
            <span className="font-hand text-tb-navy/55 text-xs">
              Want a cross-trip view of everything you're buying?
            </span>
            <a
              href="/marketplace/shopping-lists"
              data-testid="tsd-shopping-open"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-tb-navy border-2 border-tb-navy hover:bg-tb-navy hover:text-white font-hand text-xs tb-tap transition-colors"
            >
              Open shopping page
            </a>
          </div>
        </>
      ) : (
        <div className="py-8 flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white border-2 border-tb-navy/15 flex items-center justify-center">
            <ShoppingBag className="w-6 h-6 text-tb-navy" />
          </div>
          <div className="font-serif-it italic text-tb-navy text-lg">
            Shopping list is available on booked trips.
          </div>
          <a
            href="/marketplace/shopping-lists"
            data-testid="tsd-shopping-open"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy text-white border-2 border-tb-navy hover:bg-tb-navy-deep font-hand text-sm tb-tap transition-colors"
          >
            Open shopping lists
          </a>
        </div>
      )}
    </div>
  );
}

// --- Budget tab (trips + stories with source trip) ----------------
function BudgetTab({ rec, tripId, reload }) {
  const canEdit = !!tripId;
  const expenses = rec.expenses || [];
  const total = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const [form, setForm] = useState({ label: "", amount: "", category: "general" });
  const [editing, setEditing] = useState(null); // full expense object being edited
  const [busy, setBusy] = useState(false);

  const CATEGORIES = ["general", "food", "transport", "lodging", "activities", "shopping"];

  const reset = () => { setForm({ label: "", amount: "", category: "general" }); setEditing(null); };

  const submit = async () => {
    const label = (form.label || "").trim();
    const amount = parseFloat(form.amount);
    if (!label || isNaN(amount)) return toast.error("Label and amount are required");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/booked-trips/${tripId}/expenses/${editing.id}`, {
          label, amount, category: form.category || "general",
        });
        toast.success("Expense updated");
      } else {
        await api.post(`/booked-trips/${tripId}/expenses`, {
          label, amount, category: form.category || "general",
        });
        toast.success("Expense added");
      }
      reset();
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  const startEdit = (exp) => {
    setEditing(exp);
    setForm({
      label: exp.label || "",
      amount: String(exp.amount ?? ""),
      category: exp.category || "general",
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (exp) => {
    if (!canEdit) return;
    if (!window.confirm(`Delete "${exp.label}"?`)) return;
    try {
      await api.delete(`/booked-trips/${tripId}/expenses/${exp.id}`);
      toast.success("Expense removed");
      if (editing?.id === exp.id) reset();
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove");
    }
  };

  return (
    <div data-testid="tsd-tab-budget-body" className="py-4 space-y-4">
      {canEdit && (
        <div className="rounded-2xl border border-tb-navy/15 bg-white p-3 space-y-2 shadow-[0_2px_10px_-4px_rgba(26,58,95,0.15)]">
          <div className="flex items-center justify-between">
            <span className="font-serif-it italic text-tb-navy text-lg">
              {editing ? "Edit Expense" : "Add Expense"}
            </span>
            <span className="font-hand text-tb-navy/55 text-xs uppercase tracking-wider">
              Total: ${total.toFixed(2)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="Coffee at Café Central"
              className="flex-1 min-w-[140px] px-3 py-1.5 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
              data-testid="tsd-expense-label"
            />
            <input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="12.50"
              className="w-24 px-3 py-1.5 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
              data-testid="tsd-expense-amount"
            />
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="px-2 py-1.5 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
              data-testid="tsd-expense-category"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {editing && (
              <button
                type="button"
                onClick={reset}
                data-testid="tsd-expense-cancel"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-tb-navy border-2 border-tb-navy hover:bg-tb-navy hover:text-white font-hand text-xs tb-tap transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="tsd-expense-save"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy text-white border-2 border-tb-navy hover:bg-tb-navy-deep font-hand text-xs tb-tap disabled:opacity-60 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> {editing ? "Save changes" : "Add Expense"}
            </button>
          </div>
        </div>
      )}
      {expenses.length === 0 ? (
        <div className="py-6 text-center font-hand text-tb-navy/50 text-sm">
          No expenses logged yet
        </div>
      ) : (
        <ul className="space-y-1.5">
          {expenses.map((e) => (
            <li
              key={e.id}
              data-testid={`tsd-expense-item-${e.id}`}
              className="rounded-lg bg-white border border-tb-navy/15 px-3 py-2 flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <div className="font-ui text-sm text-tb-navy/90 truncate">{e.label}</div>
                <div className="font-hand text-tb-navy/50 text-xs">{e.category || "general"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-hand text-tb-navy text-sm tabular-nums">
                  ${Number(e.amount || 0).toFixed(2)}
                </span>
                {canEdit && (
                  <>
                    <button
                      type="button"
                      onClick={() => startEdit(e)}
                      data-testid={`tsd-expense-edit-${e.id}`}
                      aria-label="Edit expense"
                      className="text-tb-navy/60 hover:text-tb-navy tb-tap p-1 rounded hover:bg-tb-navy/5"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(e)}
                      data-testid={`tsd-expense-delete-${e.id}`}
                      aria-label="Delete expense"
                      className="text-tb-navy/60 hover:text-red-500 tb-tap p-1 rounded hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ==================================================================
//  Root page
// ==================================================================
export default function TripStoryDetailPage() {
  const { year, id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const editParam = new URLSearchParams(loc.search).get("edit");
  const [editing, setEditing] = useState(editParam === "1");
  const [entity, setEntity] = useState(null); // {kind, record}
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("journal");
  const [dirty, setDirty] = useState(null); // pending edits (kind, {field: value})
  const [busy, setBusy] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const yearN = parseInt(year, 10) || new Date().getFullYear();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await loadEntity(id);
      setEntity(res);
      setDirty(null);
    } catch (e) {
      toast.error("Couldn't load this trip");
      nav(`/chapters/${yearN}`, { replace: true });
    } finally { setLoading(false); }
  }, [id, nav, yearN]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setEditing(editParam === "1"); }, [editParam]);

  if (loading || !entity) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-tb-navy-deep text-tb-gold-bright font-hand text-xl">
        ~ opening your story ~
      </div>
    );
  }

  const { kind, record } = entity;
  const isTrip = kind === "trip";
  const view = dirty ? { ...record, ...dirty } : record;
  // Prefer the trip's linked promoted_story data mirroring so journal /
  // packing / etc. work on stories too.
  const tripIdForData = isTrip ? record.id : (record.booked_trip_id || record.auto_promoted_from_trip_id || null);

  const onChangeField = (k, v) => {
    setDirty((d) => ({ ...(d || {}), [k]: v }));
  };

  // Photos ------------------------------------------------------
  const photos = view.photos || [];
  const photoBase = isTrip ? `/booked-trips/${record.id}/photos` : `/stories/${record.id}/photos`;
  const addPhoto = async (url) => {
    try {
      const { data } = await api.post(photoBase, { url });
      setEntity({ kind, record: { ...record, photos: data.photos } });
      toast.success("Photo added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add photo");
    }
  };
  const removePhoto = async (url) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      const { data } = await api.delete(photoBase, { params: { url } });
      setEntity({ kind, record: { ...record, photos: data.photos } });
      toast.success("Photo removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove");
    }
  };

  // 2026-07-18 Fix 3a — Set-as-cover. Persists via
  // `PATCH /api/{stories|booked-trips}/{id}/cover` and mirrors the
  // change locally so the star flips without a reload. Passing the
  // currently-active cover URL toggles it back to the default (server
  // clears `cover_photo_id` back to null and photos[0] takes over).
  const coverBase = isTrip ? `/booked-trips/${record.id}/cover` : `/stories/${record.id}/cover`;
  const setCoverPhoto = async (url) => {
    const nextId = (record.cover_photo_id === url) ? null : url;
    try {
      const { data } = await api.patch(coverBase, { photo_id: nextId });
      setEntity({ kind, record: { ...record, cover_photo_id: data.cover_photo_id } });
      // Ping the chapter list so the timeline card refreshes its cover.
      try { window.dispatchEvent(new CustomEvent("travelbio:chapter-changed")); }
      catch { /* no-op */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't set cover");
    }
  };

  // Highlights --------------------------------------------------
  const highlights = view.highlights || [];
  const hlBase = isTrip ? `/booked-trips/${record.id}/highlights` : `/stories/${record.id}/highlights`;
  const addHighlight = async (payload) => {
    try {
      const { data } = await api.post(hlBase, payload);
      setEntity({ kind, record: { ...record, highlights: data.highlights, needs_highlights: false } });
      toast.success("Highlight added ✦");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    }
  };
  const removeHighlight = async (h) => {
    if (!window.confirm(`Remove "${h.label}"?`)) return;
    try {
      const { data } = await api.delete(`${hlBase}/${h.id}`);
      setEntity({ kind, record: { ...record, highlights: data.highlights } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove");
    }
  };

  // Header actions ---------------------------------------------
  const goBack = () => nav(`/chapters/${yearN}`);
  const enterEdit = () => nav(`${loc.pathname}?edit=1`, { replace: false });
  const exitEdit = () => { setDirty(null); nav(loc.pathname, { replace: false }); };
  const saveEdits = async () => {
    if (!dirty || Object.keys(dirty).length === 0) { exitEdit(); return; }
    setBusy(true);
    try {
      if (isTrip) {
        // Merge full body (existing PUT semantics on trips require the
        // full BookedTripIn payload — we ship what's on record + patch).
        await api.put(`/booked-trips/${record.id}`, {
          name: view.name || record.name || "",
          destination_id: record.destination_id || null,
          destination_name: view.destination_name || "",
          country: view.country || record.country || "",
          image: record.image || "",
          start_date: view.start_date || "",
          end_date: view.end_date || "",
          total_budget: record.total_budget || 0,
          notes: view.notes || "",
          trip_type: record.trip_type || "pleasure",
        });
      } else {
        await api.put(`/chapters/${record.id}`, {
          year: record.year || yearN,
          trip_name: view.trip_name || record.trip_name || "",
          country: view.country || record.country || "",
          code: record.code || "",
          icon: record.icon || "compass",
          stamp_color: record.stamp_color || "#1a3a5f",
          start_date: view.start_date || record.start_date || "",
          end_date: view.end_date || record.end_date || "",
          rating: record.rating || 5,
          image: record.image || (view.photos || [])[0] || "",
          favorite_moments: record.favorite_moments || [],
          photos: view.photos || record.photos || [],
        });
      }
      toast.success("Saved ✦");
      exitEdit();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };
  // 2026-07-17 Fix 6 — dispatch a page-level event so ChapterYearPage
  // refetches its timeline on any structural change (delete / move /
  // promote). Prevents the ghost-card bug where a deleted story
  // remained on `/chapters/:year` until manual refresh.
  const _pingChapters = () => {
    try { window.dispatchEvent(new CustomEvent("travelbio:chapter-changed")); }
    catch { /* no-op */ }
  };

  const deleteEntity = async () => {
    if (!window.confirm("Delete this? This can't be undone.")) return;
    try {
      if (isTrip) await api.delete(`/booked-trips/${record.id}`);
      else await api.delete(`/chapters/${record.id}`);
      toast.success("Deleted");
      _pingChapters();
      nav(`/chapters/${yearN}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
    }
  };
  const promote = async () => {
    if (!isTrip) return;
    try {
      const { data } = await api.post(`/booked-trips/${record.id}/promote`);
      if (data?.promoted) toast.success("Promoted to a story ✦");
      else toast.info("Not eligible for promotion yet");
      _pingChapters();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't promote");
    }
  };
  const moveEntity = async (payload) => {
    try {
      if (isTrip) await api.patch(`/booked-trips/${record.id}/move`, payload);
      else await api.patch(`/chapters/${record.id}/move`, { year: payload.year });
      toast.success(payload.trip_type === "business" ? "Moved to business" : `Moved to ${payload.year}`);
      setShowMove(false);
      _pingChapters();
      if (payload.trip_type === "business") nav("/chapters/business");
      else nav(`/chapters/${payload.year}/trip/${record.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't move");
    }
  };

  return (
    <div
      data-testid={TIDS.page}
      data-edit-mode={editing ? "1" : "0"}
      className="min-h-[100dvh] bg-tb-navy-deep w-full"
    >
      <div className="mx-auto my-4 md:my-8 max-w-[880px] px-3 md:px-6">
        <div className="rounded-2xl bg-white border border-tb-navy/15 shadow-[0_20px_50px_-16px_rgba(26,58,95,0.35)] px-4 md:px-8 pt-4 pb-6">
          <DetailHeader
            rec={view}
            kind={kind}
            year={yearN}
            editing={editing}
            onBack={goBack}
            onShare={() => setShowShare(true)}
            onEdit={enterEdit}
            onDelete={deleteEntity}
            onMove={() => setShowMove(true)}
            onPromote={promote}
            onChangeField={onChangeField}
          />

          <PhotoCarouselSection
            photos={photos}
            onAdd={addPhoto}
            onRemove={removePhoto}
            editing={editing}
            coverPhotoId={view.cover_photo_id || null}
            onSetCover={setCoverPhoto}
          />

          <HighlightsCard
            highlights={highlights}
            tripStart={record.start_date}
            tripEnd={record.end_date}
            onAdd={addHighlight}
            onRemove={removeHighlight}
            editing={editing}
          />

          {(() => {
            // Budget tab renders only when we have a resolvable trip
            // id (both real trip records and promoted stories whose
            // source trip still exists). Stories that only cache
            // `journal_entries` without a trip link don't get Budget.
            const tabs = tabsForContext({ hasBudget: !!tripIdForData });
            return (
              <>
                <TabBar tabs={tabs} active={tab} onChange={setTab} />
                <div className="min-h-[200px]">
                  {tab === "journal"   && <JournalTab   kind={kind} rec={record} tripId={tripIdForData} reload={load} />}
                  {tab === "itinerary" && <ItineraryTab kind={kind} rec={record} tripId={tripIdForData} reload={load} />}
                  {tab === "docs"      && <TravelDocsTab />}
                  {tab === "budget"    && <BudgetTab    rec={record} tripId={tripIdForData} reload={load} />}
                  {tab === "packing"   && <PackingTab   tripId={tripIdForData} />}
                  {tab === "shopping"  && <ShoppingTab  tripId={tripIdForData} />}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {editing && (
        <div
          data-testid={TIDS.editMode}
          className="fixed bottom-4 right-4 z-40 flex gap-2 rounded-2xl bg-white border-2 border-tb-navy px-3 py-2 shadow-[0_10px_30px_-8px_rgba(26,58,95,0.45)]"
        >
          <button
            type="button"
            data-testid={TIDS.cancelBtn}
            onClick={exitEdit}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-tb-navy border-2 border-tb-navy hover:bg-tb-navy hover:text-white font-hand text-sm tb-tap transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid={TIDS.saveBtn}
            disabled={busy}
            onClick={saveEdits}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy text-white border-2 border-tb-navy hover:bg-tb-navy-deep font-hand text-sm tb-tap disabled:opacity-60 transition-colors"
          >
            <Save className="w-3.5 h-3.5" />
            Save changes
          </button>
        </div>
      )}

      {showShare && (
        <ShareChapterDialog
          year={yearN}
          stories={kind === "story" ? [record] : []}
          onClose={() => setShowShare(false)}
        />
      )}
      {showMove && (
        <MoveDialog
          kind={isTrip ? "trip" : "story"}
          current={{ year: yearN, trip_type: record.trip_type || "pleasure" }}
          onClose={() => setShowMove(false)}
          onConfirm={moveEntity}
        />
      )}
    </div>
  );
}
